import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, StateUtils, StateWithProcess } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { filter, map, share, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { B2BUserConnector } from '../../../features/organization-base/connectors';
import { UserIdService } from '../../auth';
import { TranslatePipe } from '../../i18n/pipe/translate.pipe';
import {
  B2BUser,
  CreateOrUpdateOrderCardConfiguration,
  OrderCard,
  OrderCardAdminSearchParams,
  OrderCardConfiguration,
  OrderCardStatus,
  Pagination,
  UpdateOrderCardActionType,
  UserRoleList,
} from '../../model';
import { ProcessSelectors } from '../../process/store';
import { withdrawOn } from '../../util/rxjs';
import { OrderCardsAdminFacade, PrincipalConfigurationService } from '../index';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import {
  getOrderCardAdminByCodeLoading,
  getOrderCardAdminByCodeState,
  getOrderCardAdminByCodeSuccess,
  getOrderCardAdminConfigurantionEntityState,
  getOrderCardAdminConfigurantionListState,
  getOrderCardAdminConfigurantionLoadingState,
} from '../store/selectors/order-cards-admin.selectors';
import { DELETE_ORDER_CARD_PROCESS, StateWithUser } from '../store/user-state';

@Injectable()
export class OrderCardsAdminService implements OrderCardsAdminFacade {
  constructor(
    private store: Store<StateWithUser & StateWithProcess<void>>,
    private authService: AuthService,
    private userIdService: UserIdService,
    private b2bUserConnector: B2BUserConnector,
    private cxTranslate: TranslatePipe,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  getOrderCard(code: string): Observable<OrderCard> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(getOrderCardAdminByCodeState(code))),
      filter((aps) => {
        if (this.needsToLoad(aps)) {
          this.loadOrderCard(code);
          return false;
        }
        return true;
      }),
      map((aps) => {
        return aps.value;
      }),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderCardLoading(code: string): Observable<boolean> {
    return this.store.select(getOrderCardAdminByCodeLoading(code));
  }

  getOrderCardSuccess(code: string): Observable<boolean> {
    return this.store.select(getOrderCardAdminByCodeSuccess(code));
  }

  resetOrderCardState(code: string) {
    this.store.dispatch(new UserActions.LoadOrderCardAdminReset(code));
  }

  private loadOrderCard(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderCardAdmin(code, userId)),
      () => {}
    );
  }

  searchOrderCards(searchParams: OrderCardAdminSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.SearchOrderCardsAdmin({ userId, searchParams })),
      () => {}
    );
  }

  getOrderCardsSearchResult(searchParams: OrderCardAdminSearchParams): Observable<OrderCard[] | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.OrderCardsAdminSelectors.getAdminSearchResultEntries(searchParams))),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getSearchResultPagination(searchParams: OrderCardAdminSearchParams): Observable<Pagination | undefined> {
    return this.store.select(UserSelectors.OrderCardsAdminSelectors.getAdminSearchPaginationSelector(searchParams));
  }

  getSearchPageResultsLoading(searchParams: OrderCardAdminSearchParams): Observable<boolean> {
    return this.store.select(UserSelectors.OrderCardsAdminSelectors.getSearchPageResultsLoading(searchParams));
  }

  updateOrderCard(code: string, params: { name?: string; description?: string; contactPerson?: string }): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateOrderCardAdmin({ userId, code, orderCardParams: params })),
      () => {}
    );
  }

  updateOrderCardStatus(code: string, status: OrderCardStatus): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateOrderCardAdminStatus({ userId, code, status })),
      () => {}
    );
  }

  addOrderCardPrincipal(code: string, principal: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.AddOrderCardAdminPrincipal(code, userId, principal)),
      () => {}
    );
  }

  deleteOrderCardPrincipal(code: string, principal: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteOrderCardAdminPrincipal(code, userId, principal)),
      () => {}
    );
  }

  updateOrderCardPrincipals(code: string, principals: string[], action: UpdateOrderCardActionType): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateOrderCardAdminPrincipals({ userId, code, principals, action })),
      () => {}
    );
  }

  addOrderCardProduct(code: string, product: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.AddOrderCardAdminProduct(code, userId, product)),
      () => {}
    );
  }

  replaceOrderCardProduct(code: string, oldProductCode: string, newProductCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.ReplaceOrderCardAdminProduct(code, userId, oldProductCode, newProductCode)),
      () => {}
    );
  }

  addOrderCardArticles(code: string, articles: string[]): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.AddOrderCardAdminArticles(code, userId, articles)),
      () => {}
    );
  }

  deleteOrderCardProduct(code: string, product: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteOrderCardAdminProduct(code, userId, product)),
      () => {}
    );
  }

  updateOrderCardProducts(code: string, products: string[], action: UpdateOrderCardActionType): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateOrderCardAdminProducts({ userId, code, products, action })),
      () => {}
    );
  }

  loadOrderCardConfigurations(code: string, customer: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderCardAdminConfigurations(code, userId, customer)),
      () => {}
    );
  }

  deleteOrderCard(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteOrderCardAdmin({ userId, code })),
      () => {}
    );
  }

  deleteOrderCardSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(DELETE_ORDER_CARD_PROCESS)));
  }

  createOrderCard(): Observable<string> {
    const name = this.cxTranslate.transform('orderCards.createUntitledName_hint');
    const tempId = Math.random().toString(36).substr(2, 9);

    return this.userIdService.takeUserId(true).pipe(
      map((userId) => {
        this.store.dispatch(new UserActions.CreateOrderCardAdmin({ userId, orderCardParams: { name }, tempId }));
        return tempId;
      })
    );
  }

  createOrderCardsWithTempId(): Observable<OrderCard[]> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.OrderCardsAdminSelectors.getAdminSearchResultEntities)),
      map((entries) =>
        Object.entries(entries)
          .map(([, value]) => value)
          .filter((orderCard) => orderCard.tempId)
      ),
      filter((entries) => !!entries.length),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderCardConfiguration(code: string, productCode: string, customer: string): Observable<OrderCardConfiguration> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(getOrderCardAdminConfigurantionEntityState(`${code}|${productCode}`))),
      withLatestFrom(this.store.select(getOrderCardAdminConfigurantionListState(code))),
      filter(([entityState, listState]) => {
        if (this.needsToLoad(entityState) && !listState.success && !listState.loading) {
          this.loadOrderCardConfiguration(code, productCode, customer);
          return false;
        }
        return true;
      }),
      map(([entityState, _listState]) => {
        return entityState.value;
      }),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderCardConfigurationLoading(code: string, productCode: string): Observable<boolean> {
    return this.store.select(getOrderCardAdminConfigurantionLoadingState(`${code}|${productCode}`));
  }

  private loadOrderCardConfiguration(code: string, productCode: string, customer: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderCardAdminConfiguration(code, userId, productCode, customer)),
      () => {}
    );
  }

  updateOrderCardConfiguration(
    code: string,
    productCode: string,
    customer: string,
    params: CreateOrUpdateOrderCardConfiguration
  ): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(new UserActions.UpdateOrderCardAdminConfiguration(code, userId, productCode, customer, params)),
      () => {}
    );
  }

  clearOrderCardConfigurations(): void {
    this.store.dispatch(new UserActions.ClearOrderCardAdminConfigurations());
  }

  getOrderCardUsers(params): Observable<B2BUser[]> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.b2bUserConnector.getB2BUsers(userId, params)),
      map((d) => d.results),
      filter((d) => !!d.length)
    );
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<OrderCardConfiguration | OrderCard | B2BUser[] | UserRoleList>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  private hasOrderCard(): Observable<boolean> {
    return this.getOrderCardsSearchResult({ key: 'latest', count: 1, page: 0 }).pipe(
      tap((result) => {
        if (result === undefined) {
          this.searchOrderCards({ key: 'latest', count: 1, page: 0 });
        }
      }),
      map((result) => Boolean(result?.length))
    );
  }

  isOrderCardsAdminEnabled(): Observable<boolean> {
    return this.principalConfigurationService
      .isEnabled('enableOrderCardsAdmin')
      .pipe(switchMap((isEnabled) => (isEnabled ? this.hasOrderCard() : of(false))));
  }
}
